import i18n from "@/i18n.ts";
import { RuleGroup } from "@/types/rule.types.ts";
import { ApiEventVisibility } from "@/types/event.types.ts";
import { ApiWorkflowBlockTimingFields } from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiWorkflowBlockDelayReference,
  ApiWorkflowMeetingDateReference,
  ApiWorkflowTriggerScheduleDayType,
  ApiWorkflowTriggerScheduleEntityField,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { MeetingDateDirection } from "@/pages/workflows/edit/actions/workflow-meeting.helpers.ts";
import { z } from "zod";

export interface MeetingFormValues extends ApiWorkflowBlockTimingFields {
  title: string | null;
  meeting_title: string;
  organizer_id: number | null;
  calendar_id: number | null;
  start_time: string;
  duration_minutes: number;
  attendee_rule_groups: RuleGroup[];
  external_attendee_emails: string[];
  description: string | null;
  location: string | null;
  visibility: ApiEventVisibility;
  is_conference: boolean;
  meeting_date_reference: ApiWorkflowMeetingDateReference;
  meeting_date_entity_field: ApiWorkflowTriggerScheduleEntityField | null;
  meeting_date_direction: MeetingDateDirection;
  meeting_date_offset: number;
  meeting_date_day_type: ApiWorkflowTriggerScheduleDayType;
}

export const buildMeetingSchema = () => {
  const required = i18n.t("errors:field_required");

  return z
    .object({
      title: z.string().nullable(),
      meeting_title: z.string().min(1, required),
      organizer_id: z
        .number()
        .nullable()
        .refine((value) => value !== null, required),
      calendar_id: z
        .number()
        .nullable()
        .refine((value) => value !== null, required),
      start_time: z.string().min(1, required),
      duration_minutes: z
        .number({ error: i18n.t("errors:field_number_required") })
        .int()
        .min(1, i18n.t("api_errors:minimum_value_must", { minimum: 1 })),
      attendee_rule_groups: z
        .array(z.custom<RuleGroup>())
        .refine((groups) => groups.some((group) => group.rules.length > 0), required),
      external_attendee_emails: z
        .array(z.string())
        .refine(
          (emails) => emails.every((email) => z.string().email().safeParse(email).success),
          i18n.t("errors:field_email_required")
        ),
      description: z.string().nullable(),
      location: z.string().nullable(),
      visibility: z.enum(ApiEventVisibility),
      is_conference: z.boolean(),
      delay_days: z.number({ error: i18n.t("errors:field_number_required") }).min(0, required),
      delay_hours: z.number({ error: i18n.t("errors:field_number_required") }).min(0, required),
      delay_minutes: z.number({ error: i18n.t("errors:field_number_required") }).min(0, required),
      delay_reference: z.enum(ApiWorkflowBlockDelayReference),
      delay_day_type: z.enum(ApiWorkflowTriggerScheduleDayType),
      meeting_date_reference: z.enum(ApiWorkflowMeetingDateReference),
      meeting_date_entity_field: z.enum(ApiWorkflowTriggerScheduleEntityField).nullable(),
      meeting_date_direction: z.enum(["before", "after"]),
      meeting_date_offset: z
        .number({ error: i18n.t("errors:field_number_required") })
        .int()
        .min(0, i18n.t("api_errors:minimum_value_must", { minimum: 0 }))
        .max(90, i18n.t("api_errors:maximum_value_must", { maximum: 90 })),
      meeting_date_day_type: z.enum(ApiWorkflowTriggerScheduleDayType),
    })
    .refine(
      (data) =>
        data.meeting_date_reference !== ApiWorkflowMeetingDateReference.entity_date ||
        data.meeting_date_entity_field !== null,
      { path: ["meeting_date_entity_field"], error: required }
    );
};
